import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Card, EmptyState, Notice } from '@gatecheckpro/design-system';
import type { OrganizerEvent } from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';

interface VenueSummary {
  readonly venueId: string;
  readonly name: string;
  readonly publicLocality: string;
  readonly countryCode: string;
  readonly timezone: string;
}

/**
 * Choosing where the event happens.
 *
 * Changing the venue is deliberately a confirmed action rather than a dropdown
 * that saves on change: gates and zones belong to a venue, so moving the event
 * invalidates every gate policy already configured, and an organizer should know
 * that before it happens rather than after.
 */
export function EventVenuePage() {
  const { event, organizationId, reload } = useEventWorkspace();
  const command = useCommand<OrganizerEvent>();
  const base = `/api/v1/organizer/organizations/${organizationId}`;
  const { resource } = useResource<{ venues: VenueSummary[] }>(`${base}/venues`);
  const [pending, setPending] = useState<string | null>(null);

  async function chooseVenue(venueId: string): Promise<void> {
    const result = await command.run(`${base}/events/${event.eventId}`, {
      method: 'PUT',
      body: {
        title: event.title,
        publicSummary: event.publicSummary,
        publicDescription: event.publicDescription,
        internalNotes: event.internalNotes,
        venueId,
        timezone:
          resource.status === 'loaded'
            ? (resource.data.venues.find((venue) => venue.venueId === venueId)?.timezone ??
              event.timezone)
            : event.timezone,
      },
    });
    if (result !== null) {
      setPending(null);
      reload();
    }
  }

  return (
    <div className="gc-stack gc-stack--loose">
      <FailureNotice error={command.error} />

      {event.venue === null ? (
        <Notice tone="caution" title="No venue chosen">
          An event needs a venue before you can configure gates, and before you can publish.
        </Notice>
      ) : (
        <Card title={event.venue.name} eyebrow="Current venue">
          <div className="gc-stack gc-stack--tight">
            <p className="gc-muted">{event.venue.publicLocality}</p>
            <p className="gc-muted">Times for this event are in {event.timezone}.</p>
            <Link to={`/o/${organizationId}/venues/${event.venue.venueId}`}>
              Manage this venue’s zones and gates
            </Link>
          </div>
        </Card>
      )}

      {resource.status === 'loaded' && resource.data.venues.length === 0 && (
        <EmptyState
          title="No venues yet"
          action={
            <Link to={`/o/${organizationId}/venues`} className="gc-button gc-button--primary">
              Add a venue
            </Link>
          }
        >
          A venue is the physical place, reused across every event you put on there. Its zones and
          gates are configured once and then given event-specific rules here.
        </EmptyState>
      )}

      {resource.status === 'loaded' && resource.data.venues.length > 0 && (
        <Card title="Choose a venue" eyebrow="Where it happens">
          <div className="gc-stack">
            {resource.data.venues.map((venue) => {
              const isCurrent = venue.venueId === event.venue?.venueId;
              const isPending = pending === venue.venueId;
              return (
                <div key={venue.venueId} className="gc-row gc-row--between">
                  <div className="gc-stack gc-stack--tight">
                    <strong>{venue.name}</strong>
                    <span className="gc-muted">
                      {venue.publicLocality} · {venue.timezone}
                    </span>
                  </div>
                  {isCurrent ? (
                    <span className="gc-muted">Current venue</span>
                  ) : isPending ? (
                    <div className="gc-row">
                      <Button
                        variant="primary"
                        busy={command.busy}
                        onClick={() => void chooseVenue(venue.venueId)}
                      >
                        Move the event here
                      </Button>
                      <Button onClick={() => setPending(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button onClick={() => setPending(venue.venueId)}>Use this venue</Button>
                  )}
                </div>
              );
            })}

            {pending !== null && event.gatePolicies.length > 0 && (
              <Notice tone="caution" title="This will invalidate your gate configuration">
                Gates and zones belong to a venue. {event.gatePolicies.length} gate
                {event.gatePolicies.length === 1 ? '' : 's'} configured for this event will no
                longer apply, and you will need to set them up again at the new venue.
              </Notice>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
